import {
  EWarehouseCategorySuggestionKind,
  type IWarehouseCategoryInStock,
  type IWarehouseCategorySuggestion,
} from '@mahawi/eshop-common/src/types';
import { type IAICommunicatorWarehouseCategorySuggestionProposedCategory } from '@mahawi/eshop-rabbit';
import {
  call,
  delay,
  ForkEffect,
  put,
  race,
  StrictEffect,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { Socket } from 'socket.io-client';

import { send, sendRaw } from '../send-request';
import {
  categoriesLoadAll,
  categoriesWarehouseSuggestionsBatchFailed,
  categoriesWarehouseSuggestionsBatchLoad,
  categoriesWarehouseSuggestionsBatchLoadResponse,
  categoriesWarehouseSuggestionsBatchStart,
  categoriesWarehouseSuggestionsCancel,
  categoriesWarehouseSuggestionsFailed,
  categoriesWarehouseSuggestionsFinish,
  categoriesWarehouseSuggestionsPrepare,
  categoriesWarehouseSuggestionsPrepareResponse,
  categoriesWarehouseSuggestionsStart,
  categoryAdd,
  categoryWarehouseSuggestionApply,
  getLikelyProductCategories,
} from './reducer';

export const WAREHOUSE_CATEGORY_SUGGESTION_BATCH_SIZE = 25;
export const WAREHOUSE_CATEGORY_SUGGESTION_BATCH_TIMEOUT_MS = 180000;

type TPrepareResponse = ReturnType<
  typeof categoriesWarehouseSuggestionsPrepareResponse
>;
type TBatchResponse = ReturnType<
  typeof categoriesWarehouseSuggestionsBatchLoadResponse
>;

export const chunkWarehouseCategories = (
  warehouseCategories: IWarehouseCategoryInStock[],
): IWarehouseCategoryInStock[][] =>
  Array.from(
    {
      length: Math.ceil(
        warehouseCategories.length / WAREHOUSE_CATEGORY_SUGGESTION_BATCH_SIZE,
      ),
    },
    (_: unknown, index: number): IWarehouseCategoryInStock[] =>
      warehouseCategories.slice(
        index * WAREHOUSE_CATEGORY_SUGGESTION_BATCH_SIZE,
        (index + 1) * WAREHOUSE_CATEGORY_SUGGESTION_BATCH_SIZE,
      ),
  );

export const mergeProposedCategories = (
  proposedCategories: IAICommunicatorWarehouseCategorySuggestionProposedCategory[],
  suggestions: IWarehouseCategorySuggestion[],
): IAICommunicatorWarehouseCategorySuggestionProposedCategory[] =>
  suggestions.reduce(
    (
      acc: IAICommunicatorWarehouseCategorySuggestionProposedCategory[],
      { kind, name, parentCategoryUuid }: IWarehouseCategorySuggestion,
    ): IAICommunicatorWarehouseCategorySuggestionProposedCategory[] => {
      if (kind === EWarehouseCategorySuggestionKind.PAIR_EXISTING || !name) {
        return acc;
      }

      const proposedParentCategoryUuid: string | null =
        kind === EWarehouseCategorySuggestionKind.CREATE_CHILD
          ? (parentCategoryUuid ?? null)
          : null;

      if (
        acc.some(
          (
            proposed: IAICommunicatorWarehouseCategorySuggestionProposedCategory,
          ): boolean =>
            proposed.name === name &&
            proposed.parentCategoryUuid === proposedParentCategoryUuid,
        )
      ) {
        return acc;
      }

      return [...acc, { name, parentCategoryUuid: proposedParentCategoryUuid }];
    },
    proposedCategories,
  );

export function* runWarehouseCategorySuggestions(
  socket: Socket,
  action: ReturnType<typeof categoriesWarehouseSuggestionsStart>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<StrictEffect, void, any> {
  const languageTypeCode: string = action.payload.languageType.code;
  const { stage, skipCache } = action.payload;
  let proposedCategories: IAICommunicatorWarehouseCategorySuggestionProposedCategory[] =
    [];

  const {
    prepareResponse,
    cancelled: cancelledWhilePreparing,
  }: { prepareResponse?: { type: string }; cancelled?: unknown } = yield race({
    prepareResponse: call(
      sendRaw,
      socket,
      categoriesWarehouseSuggestionsPrepare(),
    ),
    cancelled: take(categoriesWarehouseSuggestionsCancel.type),
  });

  if (cancelledWhilePreparing) {
    return;
  }

  if (
    prepareResponse?.type !== categoriesWarehouseSuggestionsPrepareResponse.type
  ) {
    yield put(categoriesWarehouseSuggestionsFailed());
    return;
  }

  const { warehouseCategories } = (prepareResponse as TPrepareResponse).payload;
  const batches: IWarehouseCategoryInStock[][] =
    chunkWarehouseCategories(warehouseCategories);

  yield put(
    categoriesWarehouseSuggestionsPrepareResponse(
      warehouseCategories,
      batches.length,
    ),
  );

  for (const [batchIndex, batch] of batches.entries()) {
    const warehouseCategoryUuids: string[] = batch.map(
      ({ uuid }: IWarehouseCategoryInStock): string => uuid,
    );

    yield put(
      categoriesWarehouseSuggestionsBatchStart(
        warehouseCategoryUuids,
        batchIndex,
      ),
    );

    const {
      response,
      cancelled,
    }: { response?: { type: string }; cancelled?: unknown } = yield race({
      response: call(
        sendRaw,
        socket,
        categoriesWarehouseSuggestionsBatchLoad({
          languageTypeCode,
          stage,
          warehouseCategories: batch,
          proposedCategories,
          skipCache,
        }),
      ),
      timeout: delay(WAREHOUSE_CATEGORY_SUGGESTION_BATCH_TIMEOUT_MS),
      cancelled: take(categoriesWarehouseSuggestionsCancel.type),
    });

    if (cancelled) {
      return;
    }

    if (
      response?.type === categoriesWarehouseSuggestionsBatchLoadResponse.type
    ) {
      const batchResponse = response as TBatchResponse;

      proposedCategories = mergeProposedCategories(
        proposedCategories,
        batchResponse.payload.suggestions,
      );

      yield put(batchResponse);
    } else {
      yield put(
        categoriesWarehouseSuggestionsBatchFailed(warehouseCategoryUuids),
      );
    }
  }

  yield put(categoriesWarehouseSuggestionsFinish());
}

export function* watch(
  socket: Socket,
): Generator<ForkEffect<never>, void, string> {
  yield takeEvery(categoryAdd.type, send, socket);
  yield takeEvery(categoriesLoadAll.type, send, socket);
  yield takeEvery(getLikelyProductCategories.type, send, socket);
  yield takeLatest(
    categoriesWarehouseSuggestionsStart.type,
    runWarehouseCategorySuggestions,
    socket,
  );
  yield takeEvery(categoryWarehouseSuggestionApply.type, send, socket);
}
